/**
 * CompanyInfo - Displays basic company information
 * Compact definition-list style layout
 */
import { AgentPlatEntity } from '../../types/company'
import { Edit2, ExternalLink, Copy } from 'lucide-react'

interface CompanyInfoProps {
  entity: AgentPlatEntity
  showEditWebsite?: boolean
  onEditWebsite?: () => void
  compact?: boolean
}

function InfoRow({
  label,
  children,
  className = '',
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:gap-4 py-3 border-b border-tf-border-sub last:border-0 last:pb-0 ${className}`}>
      <dt className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide sm:w-32 flex-shrink-0 mb-0.5 sm:mb-0">
        {label}
      </dt>
      <dd className="text-sm text-tf-text sm:flex-1 min-w-0">{children}</dd>
    </div>
  )
}

export function CompanyInfo({
  entity,
  showEditWebsite = false,
  onEditWebsite,
  compact = false,
}: CompanyInfoProps) {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <dl className="divide-y-0">
      {entity.name && (
        <InfoRow label="Company">
          <span className="font-semibold text-tf-text">{entity.name}</span>
        </InfoRow>
      )}

      {entity.domain && (
        <InfoRow label="Domain">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`https://${entity.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-dark inline-flex items-center gap-1 font-medium"
            >
              {entity.domain}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            {showEditWebsite && onEditWebsite && (
              <button
                onClick={onEditWebsite}
                className="text-xs text-tf-text-muted hover:text-primary px-2 py-0.5 rounded hover:bg-primary/5 transition-colors flex items-center gap-1"
                title="Edit website URL"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            )}
          </div>
        </InfoRow>
      )}

      {entity.description && (
        <InfoRow label="Description" className="sm:flex-col sm:items-stretch">
          <p className="text-tf-text-sub leading-relaxed whitespace-pre-wrap">{entity.description}</p>
        </InfoRow>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-3 pt-1">
        {entity.type && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide">Type</span>
            <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-tf-feedback-info-bg text-tf-feedback-info-text capitalize">
              {entity.type}
            </span>
          </div>
        )}
        {entity.id && !compact && (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide flex-shrink-0">Entity ID</span>
            <code className="text-xs font-mono text-tf-text-muted bg-tf-bg-muted px-2 py-0.5 rounded truncate max-w-[140px]">
              {entity.id}
            </code>
            <button
              onClick={() => copyToClipboard(entity.id)}
              className="flex-shrink-0 p-1 text-tf-text-sub hover:text-tf-text-muted rounded hover:bg-tf-bg-hover transition-colors"
              title="Copy ID"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {!compact && entity.created_at && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide">Created</span>
            <span>
              {new Date(entity.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
        )}
      </div>
    </dl>
  )
}
